import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';
import React, { useEffect, useMemo, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, TextInput as RNTextInput, View } from 'react-native';

import { Markdown } from '@/components/common/markdown';
import { GlCodeTypeahead } from '@/components/search/gl-code-typeahead';
import { ActivityIndicator, Button, Modal, SegmentedButtons, Text, TextInput } from '@/components/ui';
import { CopyButton } from '@/components/ui/copy-button';
import {
	useAccountingPermissions,
	useGlCodes,
	useMarkWorkOrderBilled,
	usePostWorkOrderBill,
	useSkipWorkOrderBilling,
	useWorkOrderBillingPreview,
} from '@/hooks/use-accounting';
import type { GlCode, WorkOrderBillingQueueItem } from '@/types/models';
import { Alert } from '@/utils/alert';
import { formatBusinessDate } from '@/utils/date-utils';
import { formatMoneyCents, parseMoneyToCents } from '@/utils/format-money';
import { quickBooksMemo } from '@/utils/quickbooks-memo';

/**
 * ONE dialog for everything accounting does to a Ready-to-Bill work order.
 *
 * The row used to offer three links — Preview, Mark billed, Skip — each opening a different
 * modal with a different subset of the facts. Deciding how to bill a job needs all of them
 * at once: what was asked, what the customer will read, the derived lines and total, and
 * the invoice number it will get. So this modal shows the whole picture and then asks ONE
 * question, "what do you want to do with it":
 *
 *   Bill         — post a real bill here (invoice number optional, defaults to the sequence)
 *   Mark billed  — already invoiced outside the app (QuickBooks); record the reference
 *   Skip         — not billable; hide it from the queue with a reason
 *
 * The same field serves Bill and Mark billed — an invoice number is what both need — but
 * it means different things: an integer in THIS app's sequence for Bill, free text pointing
 * at the OTHER system for Mark billed. The label changes with the action so nobody types a
 * QuickBooks number into the app's own counter.
 */

export type BillAction = 'bill' | 'mark-billed' | 'skip';

/**
 * One row of the GL split — the SPREADSHEET grid, matching the manual journal-entry
 * composer: bare inputs in a thin-bordered table, a trailing blank row that spawns another
 * when typed into, a totals row, and an off-by row.
 *
 * Amount stays a STRING while it is being typed; parsing on every keystroke would fight the
 * user over a half-entered "1." or "-".
 */
interface SplitLine {
	key: string;
	glCodeId: string | null;
	/** Seeds the typeahead's visible text for an already-committed code. */
	glText?: string;
	description: string;
	amountInput: string;
}

const blankSplitLine = (): SplitLine => ({
	key: crypto.randomUUID(), glCodeId: null, description: '', amountInput: '',
});

const isSplitLineEmpty = (ln: SplitLine): boolean =>
	!ln.glCodeId && ln.description.trim() === '' && ln.amountInput.trim() === '';

/** Exactly one trailing blank row, always — the spreadsheet affordance for "add a line". */
const withTrailingBlank = (lines: SplitLine[]): SplitLine[] => {
	const last = lines[lines.length - 1];
	if (!last || !isSplitLineEmpty(last)) return [...lines, blankSplitLine()];
	let end = lines.length;
	while (end > 1 && isSplitLineEmpty(lines[end - 2])) end--;
	return lines.slice(0, end);
};

export const BillWorkOrderModal: React.FC<{
	target: WorkOrderBillingQueueItem | null;
	onClose: () => void;
	/** After any of the three actions succeeds — the queue must refetch. */
	onChanged: () => void | Promise<unknown>;
}> = ({ target, onClose, onChanged }) => {
	if (!target) return null;
	// Remount per work order so draft state (action, typed number, reason) never carries
	// from one job to the next — the bug the old PreviewModal needed a useEffect reset for.
	return <BillWorkOrderModalBody key={target.workOrderId} target={target} onClose={onClose} onChanged={onChanged} />;
};

const BillWorkOrderModalBody: React.FC<{
	target: WorkOrderBillingQueueItem;
	onClose: () => void;
	onChanged: () => void | Promise<unknown>;
}> = ({ target, onClose, onChanged }) => {
	const { canModify } = useAccountingPermissions();
	const { data: preview, isLoading } = useWorkOrderBillingPreview(target.workOrderId);
	const postOne = usePostWorkOrderBill();
	const markBilled = useMarkWorkOrderBilled();
	const skip = useSkipWorkOrderBilling();

	const [action, setAction] = useState<BillAction>('bill');
	const [invoiceNumber, setInvoiceNumber] = useState('');
	const [skipReason, setSkipReason] = useState('');
	const [busy, setBusy] = useState(false);
	/**
	 * The GL split, ALWAYS shown and always what gets posted.
	 *
	 * Seeded one row per DERIVED line, not collapsed per code: an untouched grid then posts
	 * byte-for-byte what the derived path posted before this editor existed. Collapsing would
	 * silently coarsen every bill's detail from here on.
	 */
	const [split, setSplit] = useState<SplitLine[]>([]);
	const [seeded, setSeeded] = useState(false);

	// Clear the shared field when the action flips: "4471" meant for QuickBooks must not be
	// submitted as an override of the app's own sequence a moment later.
	useEffect(() => { setInvoiceNumber(''); }, [action]);

	const memo = useMemo(() => quickBooksMemo(target), [target]);

	/** `id → "6000 — Repairs"`, so a seeded row shows its code rather than a bare UUID. */
	const { data: glCodesResp } = useGlCodes({ activeOnly: true, limit: 1000 });
	const glLabel = useMemo(() => new Map<string, string>(
		((glCodesResp?.data ?? []) as GlCode[]).map((c) => [c.id, `${c.code} — ${c.name}`]),
	), [glCodesResp]);

	/**
	 * Seeds once, and only when BOTH queries have landed.
	 *
	 * `seedText` is what the typeahead DISPLAYS for an already-committed code, so seeding
	 * before the chart arrives would leave every GL cell looking empty on a row that is in
	 * fact correctly coded — and re-seeding later would discard whatever had been typed.
	 */
	useEffect(() => {
		if (seeded || !preview || !glCodesResp) return;
		setSplit(withTrailingBlank(preview.lines.map((ln) => ({
			key: crypto.randomUUID(),
			glCodeId: ln.glCodeId,
			glText: glLabel.get(ln.glCodeId),
			description: ln.description,
			amountInput: (ln.amountCents / 100).toFixed(2),
		}))));
		setSeeded(true);
	}, [preview, glCodesResp, glLabel, seeded]);

	/**
	 * The authoritative figure comes from the preview. The queue row's own estimate stands in
	 * until it lands — but only if there IS one: the work-order detail page has no estimate to
	 * pass, and printing "$0.00" on a dialog about to bill somebody is a wrong number stated
	 * as fact.
	 */
	const amountText = preview
		? formatMoneyCents(preview.totalCents)
		: target.estimatedTotalCents > 0 ? formatMoneyCents(target.estimatedTotalCents) : 'Calculating…';

	/**
	 * The split REALLOCATES, it never re-prices — so it has to add up to the derived total,
	 * and Post bill stays disabled until it does. The server enforces the same rule; this is
	 * here so the difference is visible while it is being typed rather than arriving as a 400.
	 */
	const filledSplit = useMemo(() => split.filter((ln) => !isSplitLineEmpty(ln)), [split]);
	const splitTotal = useMemo(() => filledSplit.reduce((sum, ln) => {
		const cents = parseMoneyToCents(ln.amountInput);
		return sum + (Number.isFinite(cents) ? cents : 0);
	}, 0), [filledSplit]);
	const targetCents = preview?.totalCents ?? 0;
	const splitDiff = splitTotal - targetCents;
	const splitBalances = !!preview && splitDiff === 0;
	// A part-typed row (an amount but no code, or a code but no amount) is not postable.
	const splitComplete = filledSplit.length > 0
		&& filledSplit.every((ln) => !!ln.glCodeId && parseMoneyToCents(ln.amountInput) !== 0);

	const updateSplitLine = (key: string, patch: Partial<SplitLine>) =>
		setSplit((arr) => withTrailingBlank(arr.map((ln) => (ln.key === key ? { ...ln, ...patch } : ln))));
	const removeSplitLine = (key: string) =>
		setSplit((arr) => withTrailingBlank(arr.filter((ln) => ln.key !== key)));

	const run = async () => {
		setBusy(true);
		try {
			if (action === 'bill') {
				const trimmed = invoiceNumber.trim();
				const override = trimmed ? Number(trimmed) : undefined;
				if (trimmed && (!Number.isInteger(override) || (override ?? 0) <= 0)) {
					Alert.alert('Invalid number', 'The invoice number must be a positive whole number.');
					return;
				}
				const result = await postOne.mutateAsync({
					workOrderId: target.workOrderId,
					body: {
						...(override ? { invoiceNumber: override } : {}),
						// The grid is always what posts — untouched, it is the derived split.
						lines: filledSplit.map((ln) => ({
							glCodeId: ln.glCodeId as string,
							amountCents: parseMoneyToCents(ln.amountInput),
							description: ln.description.trim() || undefined,
						})),
					},
				});
				onClose();
				await onChanged();
				Alert.alert('Invoice created', `${result.billNumber} · ${formatMoneyCents(result.totalCents)}`);
				router.push(`/accounting/bill/${result.billId}`);
			} else if (action === 'mark-billed') {
				await markBilled.mutateAsync({ workOrderId: target.workOrderId, reference: invoiceNumber.trim() || undefined });
				onClose();
				await onChanged();
			} else {
				await skip.mutateAsync({ workOrderId: target.workOrderId, reason: skipReason.trim() || undefined });
				onClose();
				await onChanged();
			}
		} catch (e) {
			Alert.alert(
				action === 'bill' ? 'Could not post' : action === 'mark-billed' ? 'Could not mark billed' : 'Could not skip',
				(e as Error).message,
			);
		} finally {
			setBusy(false);
		}
	};

	const primaryLabel = busy
		? 'Working…'
		: action === 'bill' ? 'Post bill' : action === 'mark-billed' ? 'Mark as billed' : 'Skip';
	const where = [target.associationName, target.jobLocation].filter(Boolean);

	return (
		<Modal visible onDismiss={onClose} onSubmit={canModify && !busy ? run : undefined}>
			<View style={styles.header}>
				<View style={styles.iconWrap}><Ionicons name="cash-outline" size={20} color="#2e7d32" /></View>
				<View style={{ flex: 1, minWidth: 0 }}>
					<Text style={styles.title} testID="bill-modal-title">Bill work order #{target.orderNumber ?? ''}</Text>
					<Text style={styles.where} numberOfLines={2}>
						{where.map((w, i) => (
							<React.Fragment key={String(w)}>
								{i > 0 && <Text style={styles.dim}>{'  ·  '}</Text>}
								<Text style={i === 0 ? styles.association : undefined}>{w}</Text>
							</React.Fragment>
						))}
						{target.completedAt ? <Text style={styles.dim}>{'  ·  completed '}{formatBusinessDate(target.completedAt)}</Text> : null}
					</Text>
				</View>
			</View>

			<View style={styles.amountRow}>
				<View>
					<Text style={styles.amountLabel}>Amount</Text>
					{preview && preview.adjustmentCents !== 0 && (
						<Text style={styles.amountNote}>
							Raw cost {formatMoneyCents(preview.computedTotalCents)}, reconciled to the agreed charge
						</Text>
					)}
				</View>
				<Text style={styles.amount} testID="bill-modal-amount">{amountText}</Text>
			</View>

			{/* focusable={false}: RN-web otherwise draws a focus ring around the whole scroll region on click. */}
			{/* focusable={false}: RN-web otherwise draws a focus ring around the whole scroll region on click. */}
			<ScrollView style={styles.body} focusable={false}>
				{/*
					THE GL SPLIT LEADS, and it is always here — editable when this viewer can post,
					read-only otherwise. It is the one thing on this dialog that has to be right
					before Post bill, and the descriptions below it can run to a screenful, which
					is exactly how an earlier version of this buried it.
				*/}
				<View style={styles.section}>
					<Text style={styles.sectionTitle}>Bill lines · GL split</Text>
					{isLoading || !preview ? (
						<ActivityIndicator style={{ marginVertical: 12 }} />
					) : (
						<View style={styles.grid}>
							<View style={[styles.gridRow, styles.gridHeader]}>
								<View style={[styles.gridCell, styles.colGl]}>
									<Text style={styles.gridHeaderLabel}>GL code</Text>
								</View>
								<View style={[styles.gridCell, styles.colDesc]}>
									<Text style={styles.gridHeaderLabel}>Description</Text>
								</View>
								<View style={[styles.gridCell, styles.colAmt, styles.colLast]}>
									<Text style={[styles.gridHeaderLabel, styles.right]}>Amount</Text>
								</View>
								<View style={styles.colTrash} />
							</View>

							{split.map((ln, i) => (
								<View key={ln.key} style={styles.gridRow}>
									<View style={[styles.gridCell, styles.colGl, styles.glCell]}>
										{canModify ? (
											<GlCodeTypeahead
												value={ln.glCodeId}
												seedText={ln.glText}
												onSelect={(g) => updateSplitLine(ln.key, { glCodeId: g?.id ?? null, glText: undefined })}
												inputStyle={styles.bareInput}
												placeholder="GL code…"
												testID={`split-gl-${i}`}
											/>
										) : (
											<Text style={styles.bareText}>{ln.glCodeId ? glLabel.get(ln.glCodeId) ?? '—' : '—'}</Text>
										)}
									</View>
									<View style={[styles.gridCell, styles.colDesc]}>
										{canModify ? (
											<RNTextInput
												value={ln.description}
												onChangeText={(description) => updateSplitLine(ln.key, { description })}
												placeholder=""
												style={styles.bareInput}
												testID={`split-desc-${i}`}
											/>
										) : (
											<Text style={styles.bareText} numberOfLines={2}>{ln.description}</Text>
										)}
									</View>
									<View style={[styles.gridCell, styles.colAmt, styles.colLast]}>
										{canModify ? (
											<RNTextInput
												value={ln.amountInput}
												onChangeText={(amountInput) => updateSplitLine(ln.key, { amountInput })}
												placeholder="0.00"
												placeholderTextColor="#b0bec5"
												keyboardType="decimal-pad"
												style={[styles.bareInput, styles.right]}
												testID={`split-amount-${i}`}
											/>
										) : (
											<Text style={[styles.bareText, styles.right]}>{ln.amountInput}</Text>
										)}
									</View>
									<View style={styles.colTrash}>
										{canModify && !isSplitLineEmpty(ln) && (
											<Pressable
												onPress={() => removeSplitLine(ln.key)}
												hitSlop={8}
												testID={`split-remove-${i}`}
											>
												<Ionicons name="trash-outline" size={16} color="#c62828" />
											</Pressable>
										)}
									</View>
								</View>
							))}

							<View style={[styles.gridRow, styles.gridTotals]}>
								<View style={[styles.gridCell, styles.colGl]} />
								<View style={[styles.gridCell, styles.colDesc]}>
									<Text style={[styles.gridTotalsLabel, styles.right]}>Split total</Text>
								</View>
								<View style={[styles.gridCell, styles.colAmt, styles.colLast]}>
									<Text style={[styles.gridTotalsAmt, styles.right]} testID="split-total">
										{formatMoneyCents(splitTotal)}
									</Text>
								</View>
								<View style={styles.colTrash} />
							</View>
							<View style={[styles.gridRow, styles.gridTotals, styles.gridLastRow]}>
								<View style={[styles.gridCell, styles.colGl]} />
								<View style={[styles.gridCell, styles.colDesc]}>
									<Text style={[styles.gridTotalsLabel, styles.right]}>
										{splitBalances ? 'Matches the work order' : 'Difference'}
									</Text>
								</View>
								<View style={[styles.gridCell, styles.colAmt, styles.colLast]}>
									<Text
										style={[styles.gridTotalsAmt, styles.right, splitBalances ? styles.diffOk : styles.diffBad]}
										testID="split-difference"
									>
										{splitBalances
											? formatMoneyCents(0)
											: `${splitDiff > 0 ? '+' : '−'}${formatMoneyCents(Math.abs(splitDiff))}`}
									</Text>
								</View>
								<View style={styles.colTrash} />
							</View>
						</View>
					)}
					{!!preview && !splitBalances && (
						<Text style={styles.mismatchHint} testID="split-mismatch">
							The split must total {formatMoneyCents(targetCents)} — this work order&apos;s billable amount.
							A split reallocates the bill across codes; to change what is billed, edit the work
							order&apos;s final charge.
						</Text>
					)}
					{!!preview && splitBalances && !splitComplete && (
						<Text style={styles.mismatchHint}>Every line needs a GL code and an amount.</Text>
					)}
				</View>

				<Section title="Work requested" empty="No request text recorded.">{target.workRequested ?? undefined}</Section>
				<Section title="Customer will see" empty="No customer description yet — the invoice memo will be blank.">
					{target.customerDescription ?? undefined}
				</Section>

				<View style={styles.section}>
					<View style={styles.memoHeader}>
						<Text style={styles.sectionTitle}>QuickBooks description</Text>
						<CopyButton text={memo} />
					</View>
					<Text style={styles.memo} selectable testID="bill-modal-memo">{memo}</Text>
				</View>
			</ScrollView>

			{canModify && (
				<View style={styles.actionBlock}>
					<SegmentedButtons
						value={action}
						onValueChange={(v) => setAction(v as BillAction)}
						buttons={[
							{ value: 'bill', label: 'Bill', testID: 'action-bill' },
							{ value: 'mark-billed', label: 'Mark billed', testID: 'action-mark-billed' },
							{ value: 'skip', label: 'Skip', testID: 'action-skip' },
						]}
					/>
					{action === 'bill' && (
						<>
							<Text style={styles.actionHelp}>
								Creates the bill and posts it to the ledger. Leave the number blank to take the next in
								the sequence{preview ? ` (${preview.invoicePrefix}${preview.nextInvoiceNumber})` : ''}.
							</Text>
							<TextInput
								label="Invoice number (optional override)"
								value={invoiceNumber}
								onChangeText={setInvoiceNumber}
								placeholder={preview ? String(preview.nextInvoiceNumber) : ''}
								keyboardType="number-pad"
								testID="input-invoice-number"
							/>
						</>
					)}
					{action === 'mark-billed' && (
						<>
							<Text style={styles.actionHelp}>
								Already invoiced outside the app. Nothing is posted here; the work order leaves the queue
								and can be restored from its Cost Summary.
							</Text>
							<TextInput
								label="Outside invoice number or reference (optional)"
								value={invoiceNumber}
								onChangeText={setInvoiceNumber}
								placeholder="QB invoice 4471"
								testID="input-mark-billed-reference"
							/>
						</>
					)}
					{action === 'skip' && (
						<>
							<Text style={styles.actionHelp}>
								Not billable — covered by the management fee, for example. Hides it from the queue; it
								stays on the Skipped tab where it can be restored.
							</Text>
							<TextInput
								label="Reason (optional)"
								value={skipReason}
								onChangeText={setSkipReason}
								placeholder="Covered by the monthly management fee"
								testID="input-skip-reason"
							/>
						</>
					)}
				</View>
			)}

			<View style={styles.actions}>
				<Button mode="outlined" onPress={onClose} disabled={busy} testID="bill-modal-cancel">
					<Text>{canModify ? 'Cancel' : 'Close'}</Text>
				</Button>
				{canModify && (
					<Button
						mode="contained"
						variant={action === 'skip' ? 'primary' : 'success'}
						onPress={() => void run()}
						disabled={busy || (action === 'bill' && (isLoading || !splitBalances || !splitComplete))}
						testID="bill-modal-confirm"
					>
						{/* A string, not <Text>: a nested Text forces its own dark colour over the label's. */}
						{primaryLabel}
					</Button>
				)}
			</View>
		</Modal>
	);
};

const Section: React.FC<{ title: string; empty: string; children?: string }> = ({ title, empty, children }) => (
	<View style={styles.section}>
		<Text style={styles.sectionTitle}>{title}</Text>
		{children
			? <View style={styles.sectionBody}><Markdown>{children}</Markdown></View>
			: <Text style={styles.sectionEmpty}>{empty}</Text>}
	</View>
);

/** Same hairline the journal-entry grid uses, so the two tables read as one control. */
const GRID_BORDER = '#e0e4e8';

const styles = StyleSheet.create({
	header: { flexDirection: 'row', alignItems: 'flex-start', gap: 12, marginBottom: 12 },
	iconWrap: {
		width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center',
		backgroundColor: '#e8f5e9',
	},
	title: { fontSize: 18, fontWeight: '700', color: '#263238' },
	where: { fontSize: 13, color: '#37474f', marginTop: 2 },
	association: { fontWeight: '600' },
	dim: { color: '#90a4ae' },
	amountRow: {
		flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12,
		paddingVertical: 10, paddingHorizontal: 12, borderRadius: 8, backgroundColor: '#e8f5e9', marginBottom: 12,
	},
	amountLabel: { fontSize: 13, fontWeight: '600', color: '#2e7d32' },
	amountNote: { fontSize: 11, color: '#558b2f', marginTop: 2 },
	amount: { fontSize: 20, fontWeight: '700', color: '#2e7d32', fontVariant: ['tabular-nums'] },
	body: { maxHeight: 360 },
	section: { marginBottom: 12 },
	sectionTitle: {
		fontSize: 11, fontWeight: '700', letterSpacing: 0.6, textTransform: 'uppercase', color: '#78909c', marginBottom: 4,
	},
	sectionBody: {
		paddingVertical: 8, paddingHorizontal: 12, borderRadius: 8,
		backgroundColor: '#f5f7f8', borderLeftWidth: 3, borderLeftColor: '#cfd8dc',
	},
	sectionEmpty: { fontSize: 13, fontStyle: 'italic', color: '#90a4ae' },
	linesBox: { borderRadius: 8, borderWidth: 1, borderColor: '#e0e0e0', paddingHorizontal: 12, paddingVertical: 6 },
	lineGroup: { marginBottom: 6 },
	lineGroupLabel: { fontSize: 11, fontWeight: '600', color: '#546e7a', marginTop: 4, marginBottom: 2 },
	line: { flexDirection: 'row', justifyContent: 'space-between', gap: 12, paddingVertical: 3 },
	lineDesc: { flex: 1, minWidth: 0, fontSize: 13, color: '#37474f' },
	lineAmount: { fontSize: 13, fontVariant: ['tabular-nums'], color: '#37474f' },
	lineTotal: { borderTopWidth: 1, borderTopColor: '#e0e0e0', marginTop: 4, paddingTop: 8 },
	lineTotalLabel: { fontSize: 13, fontWeight: '700' },
	lineTotalAmount: { fontSize: 13, fontWeight: '700', fontVariant: ['tabular-nums'] },
	mismatchHint: { color: '#c62828', fontSize: 12, marginTop: 6, lineHeight: 16 },

	/**
	 * The spreadsheet grid, matching the manual journal-entry composer: one thin border round
	 * the table, cells divided by hairlines, and BARE inputs inside them so a row reads as a
	 * spreadsheet row rather than a stack of form fields.
	 */
	grid: { borderWidth: 1, borderColor: GRID_BORDER, backgroundColor: '#fff' },
	gridRow: {
		flexDirection: 'row', alignItems: 'stretch',
		borderBottomWidth: 1, borderBottomColor: GRID_BORDER, minHeight: 32,
	},
	gridLastRow: { borderBottomWidth: 0 },
	gridHeader: { backgroundColor: '#fafbfc' },
	gridHeaderLabel: {
		fontSize: 10, fontWeight: '700', color: '#546e7a', textTransform: 'uppercase', letterSpacing: 0.4,
	},
	gridCell: {
		justifyContent: 'center', paddingHorizontal: 6, paddingVertical: 4,
		borderRightWidth: 1, borderRightColor: GRID_BORDER,
	},
	colLast: { borderRightWidth: 0 },
	// minWidth 0 throughout: an input's intrinsic width otherwise wins over the flex ratio
	// and pushes the amount column off the row.
	colGl: { flex: 3, minWidth: 0 },
	colDesc: { flex: 4, minWidth: 0 },
	colAmt: { flex: 2, minWidth: 84 },
	colTrash: { width: 28, alignItems: 'center', justifyContent: 'center' },
	// The typeahead portals its dropdown, so its cell must not clip.
	glCell: { overflow: 'visible' },
	bareInput: { fontSize: 13, paddingVertical: 2, paddingHorizontal: 0, borderWidth: 0, backgroundColor: 'transparent' },
	bareText: { fontSize: 13, color: '#37474f' },
	right: { textAlign: 'right' },
	gridTotals: { backgroundColor: '#fafbfc' },
	gridTotalsLabel: {
		fontSize: 10, fontWeight: '700', color: '#546e7a', textTransform: 'uppercase', letterSpacing: 0.4,
	},
	gridTotalsAmt: { fontSize: 13, fontWeight: '700', fontVariant: ['tabular-nums'] },
	diffOk: { color: '#2e7d32' },
	diffBad: { color: '#c62828' },
	memoHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
	memo: {
		fontSize: 13, fontVariant: ['tabular-nums'], paddingVertical: 6, paddingHorizontal: 8,
		backgroundColor: '#f4f6f8', borderRadius: 6,
	},
	actionBlock: { marginTop: 8, gap: 8 },
	actionHelp: { fontSize: 12, color: '#607d8b', lineHeight: 17 },
	actions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 8, marginTop: 16 },
});
